import { useState } from 'react';
import { ChevronRight, Plus, ShieldCheck } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { Sheet, Button } from '../ui';
import { RoleSheet, type Role } from './RoleSheet';
import type { Registry } from './permissions';

/** Every role, opened from the Users page. A row opens the role's editor on top; "New role" starts a blank one. */
export function RolesSheet({ roles, registry, onClose }: { roles: Role[]; registry: Registry; onClose: () => void }) {
  const canCreate = useCan('roles.create');
  const [editing, setEditing] = useState<Role | 'new' | null>(null);
  // Re-read from the list so an edited role shows its saved values when reopened.
  const current = editing && editing !== 'new' ? roles.find((r) => r.id === editing.id) ?? null : null;

  return (
    <>
      <Sheet open onClose={onClose} size="md" eyebrow="Users" title="Roles" subtitle="What each role may do. Pick a role for a person on their profile."
        headerExtra={canCreate && <Button size="sm" onClick={() => setEditing('new')}><Plus className="h-3.5 w-3.5" /> New role</Button>}>
        <ul className="divide-y divide-ink-100 overflow-hidden rounded-lg border border-ink-200">
          {roles.map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setEditing(r)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition hover:bg-ink-50/60">
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5 text-[13px] font-medium text-ink-900">
                    {r.name}
                    {r.allAccess && <ShieldCheck className="h-3.5 w-3.5 text-bronze-500" aria-label="Full access" />}
                    {r.isSystem && <span className="text-[10.5px] font-normal uppercase tracking-wide text-ink-400">default</span>}
                  </span>
                  {r.description && <span className="mt-0.5 block truncate text-[11.5px] text-ink-500">{r.description}</span>}
                  <span className="mt-0.5 block text-[11px] text-ink-400">{r.userCount} user{r.userCount === 1 ? '' : 's'} · scope {r.scopeType.toLowerCase()}</span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-300" />
              </button>
            </li>
          ))}
          {!roles.length && <li className="px-3.5 py-6 text-center text-[12.5px] text-ink-400">No roles yet.</li>}
        </ul>
      </Sheet>
      {editing && (editing === 'new' || current) && (
        <RoleSheet key={editing === 'new' ? 'new' : editing.id} role={editing === 'new' ? null : current} roles={roles} registry={registry} onClose={() => setEditing(null)} />
      )}
    </>
  );
}
